/**
 * StatusUpdateComposeModal
 *
 * Asana-style weekly status update composer for a portfolio client.
 * POSTs to /api/agency/portfolio/status-update on publish, fetches per-team
 * history from GET /api/agency/portfolio/status-updates/:teamId for the
 * History panel.
 *
 * Features:
 * - Color pill status selector (drives Team.lifecycleStage on save)
 * - TL;DR headline with character counter
 * - Vibe selector (emoji)
 * - Output card (posts published / planned, defaults from cadence target)
 * - Top performer picker — sourced from live agency highlights
 * - Wins / Blockers / Next focus / Strategic note textareas
 * - Sticky header + footer, scrollable body
 * - Auto-save draft to localStorage every 5s when dirty
 * - History panel — live data, fetched on open via useStatusUpdateHistory
 * - Desktop: centered modal (max-w-2xl), Mobile: bottom sheet
 * - Cmd/Ctrl+Enter publishes
 * - Discard confirmation when dirty + backdrop click
 */

import { usePortfolioLocks } from '@/hooks/usePortfolioLocks';
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { getStackZIndex, useModalStackDepth } from '@/contexts/ModalStackContext';
import { useStatusUpdateHistory, useStatusUpdateMetrics } from '@/hooks/usePortfolio';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { showToast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Tooltip } from '@/components/ui/Tooltip';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { EmojiAutocompleteField } from '@/components/ui/EmojiAutocompleteField';
import { GlossaryTooltip } from './GlossaryTooltip';
import type { PortfolioClient } from '@/data/portfolio.types';
import type { StatusUpdateDraft, ColorPill, VibeType } from '@/types/portfolio';
import { getContrastTextColor } from '@/utils/colors';
import { avatarTextClass } from '@/utils/avatarContrast';
import {
  formatPeriodLabel,
  isWeekSpan,
  daysInPeriod,
  toDateInputValue,
  fromDateInputValue,
  isoToYmd,
} from '@/utils/statusUpdatePeriod';
import { statusPillLabel, statusVibeLabel } from '@/utils/portfolioLabels';
import { lockBodyScroll } from '@/hooks/useBodyScrollLock';

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * Display shape for the top-post picker list. Sourced from live agency
 * highlights via `availableTopPosts` — the modal flattens each candidate
 * into this struct so the rendering doesn't have to care where the data
 * came from.
 */
interface TopPostDisplay {
  id: string;
  caption: string;
  /** Headline metric ("12.4k engagements" / "5.2k reach" / "Published") */
  metric: string;
  /** Engagement-rate string ("4.7%" or "—" if no ER) */
  er: string;
  /** Two hex stops parsed from `thumbnailGradient`; fall back to brand teal */
  gradientFrom: string;
  gradientTo: string;
}


/** Top-post candidate sourced from live agency highlights (filtered to this team). */
export interface TopPostCandidate {
  id: string;
  caption: string;
  metric: string;
  engagementRate?: number;
  thumbnailGradient: string;
}

export interface StatusUpdateComposeModalProps {
  client: PortfolioClient;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (update: StatusUpdateDraft) => void | Promise<void>;
  existingUpdate?: StatusUpdateDraft | null;
  /** Real top-posts for this team. When empty, the top-post picker is hidden. */
  availableTopPosts?: TopPostCandidate[];
  /** Triggered when user clicks the History icon — parent opens the dedicated
   *  StatusUpdateHistoryModal so the rich detail view is reused everywhere. */
  onShowHistory?: () => void;
}

// ── Color pill config ─────────────────────────────────────────────────────────

interface PillConfig {
  value: ColorPill;
  emoji: string;
  bg: string;
  text: string;
  ring: string;
  selectedBg: string;
  tooltip: string;
  /** Glossary term ID - when set, wraps pill in GlossaryTooltip instead of plain Tooltip */
  glossaryTermId?: string;
}

// Tooltip keys are looked up at render time via t() in the component
const PILL_CONFIGS: PillConfig[] = [
  {
    value: 'ON_TRACK',
    emoji: '🟢',
    bg: 'bg-primary-50',
    text: 'text-primary-700',
    ring: 'ring-primary-400',
    selectedBg: 'bg-primary-100',
    tooltip: 'status_compose_tooltip_pill_on_track',
    glossaryTermId: 'tier-on-track',
  },
  {
    value: 'WATCH',
    emoji: '🟡',
    bg: 'bg-peach/30',
    text: 'text-coral-700',
    ring: 'ring-coral-400',
    selectedBg: 'bg-peach/60',
    tooltip: 'status_compose_tooltip_pill_watch',
    glossaryTermId: 'tier-watch',
  },
  {
    value: 'AT_RISK',
    emoji: '🟠',
    bg: 'bg-coral-50',
    text: 'text-coral-700',
    ring: 'ring-coral-400',
    selectedBg: 'bg-coral-100',
    tooltip: 'status_compose_tooltip_pill_at_risk',
    glossaryTermId: 'tier-at-risk',
  },
  {
    value: 'OFF_TRACK',
    emoji: '🔴',
    bg: 'bg-coral-50',
    text: 'text-coral-700',
    ring: 'ring-coral-400',
    selectedBg: 'bg-coral-100',
    tooltip: 'status_compose_tooltip_pill_off_track',
    glossaryTermId: 'tier-off-track',
  },
  {
    value: 'ON_HOLD',
    emoji: '⏸️',
    bg: 'bg-gray-50',
    text: 'text-sage-gray',
    ring: 'ring-gray-400',
    selectedBg: 'bg-gray-200',
    tooltip: 'status_compose_tooltip_pill_on_hold',
    glossaryTermId: 'tier-on-hold',
  },
  {
    value: 'PAUSED',
    emoji: '⏯️',
    bg: 'bg-gray-50',
    text: 'text-gray-500',
    ring: 'ring-gray-300',
    selectedBg: 'bg-gray-100',
    tooltip: 'status_compose_tooltip_pill_paused',
    glossaryTermId: 'tier-paused',
  },
  {
    value: 'COMPLETE',
    emoji: '✅',
    bg: 'bg-primary-25',
    text: 'text-primary-800',
    ring: 'ring-primary-600',
    selectedBg: 'bg-primary-50',
    tooltip: 'status_compose_tooltip_pill_complete',
    glossaryTermId: 'tier-complete',
  },
];

// ── Vibe config ───────────────────────────────────────────────────────────────

interface VibeConfig {
  value: VibeType;
  emoji: string;
  selectedBg: string;
  selectedText: string;
  tooltip: string;
}

const VIBE_CONFIGS: VibeConfig[] = [
  {
    value: 'STRONG',
    emoji: '😀',
    selectedBg: 'bg-primary-50',
    selectedText: 'text-primary-700',
    tooltip: 'status_compose_tooltip_vibe_strong',
  },
  {
    value: 'NEUTRAL',
    emoji: '😐',
    selectedBg: 'bg-gray-100',
    selectedText: 'text-gray-700',
    tooltip: 'status_compose_tooltip_vibe_neutral',
  },
  {
    value: 'TENSE',
    emoji: '😬',
    selectedBg: 'bg-peach/30',
    selectedText: 'text-coral-700',
    tooltip: 'status_compose_tooltip_vibe_tense',
  },
  {
    value: 'ON_FIRE',
    emoji: '🔥',
    selectedBg: 'bg-coral-50',
    selectedText: 'text-coral-700',
    tooltip: 'status_compose_tooltip_vibe_on_fire',
  },
];

// ── Get week start (Monday) ───────────────────────────────────────────────────

function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday = 0 offset
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

/** Monday-anchored 7-day window (end = start + 7 days - 1ms) containing `date`. */
function getWeekEnd(start: Date): Date {
  return new Date(start.getTime() + 7 * ONE_DAY_MS - 1);
}

/** Custom range span cap (matches backend validation). */
const MAX_CUSTOM_RANGE_DAYS = 92;

// ── LocalStorage helpers ──────────────────────────────────────────────────────

function getDraftKey(clientId: number): string {
  return `portfolio-status-draft-${clientId}`;
}

function loadDraft(clientId: number): Partial<StatusUpdateDraft> | null {
  try {
    const raw = localStorage.getItem(getDraftKey(clientId));
    if (!raw) return null;
    return JSON.parse(raw) as Partial<StatusUpdateDraft>;
  } catch {
    return null;
  }
}

function saveDraft(clientId: number, draft: Partial<StatusUpdateDraft>): void {
  try {
    localStorage.setItem(getDraftKey(clientId), JSON.stringify(draft));
  } catch {
    // Silently ignore storage errors
  }
}

function clearDraft(clientId: number): void {
  try {
    localStorage.removeItem(getDraftKey(clientId));
  } catch {
    // Silently ignore storage errors
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

export const StatusUpdateComposeModal = ({
  client,
  isOpen,
  onClose,
  onSave,
  existingUpdate,
  availableTopPosts = [],
  onShowHistory,
}: StatusUpdateComposeModalProps) => {
  const { t } = useTranslation('agency');
  // Cadence locked by the agency package: never default "planned" from the (zeroed) cadence target
  const { isLocked } = usePortfolioLocks();
  const cadenceLocked = isLocked('portfolio_cadence');
  const velocityLocked = isLocked('portfolio_velocity');

  // Stack-aware z-index so modals opened on top of this one (e.g. the
  // StatusUpdateHistoryModal launched via the History icon) sit above it.
  const stackDepth = useModalStackDepth(isOpen);
  const { backdrop: backdropZ, content: contentZ } = getStackZIndex(stackDepth);

  // Fetch one entry just to know whether history exists. React Query dedupes
  // this with the History modal's own fetch when both are open.
  const { data: historyProbe = [] } = useStatusUpdateHistory(
    isOpen && client ? client.id : null,
    1,
  );
  const hasHistory = historyProbe.length > 0;

  // ── Period (week / custom range) ──
  // Initialize from `existingUpdate` when present (editing a past update),
  // else default to the current Monday-anchored week. Mode is derived from
  // the resolved span so a restored custom range shows the range picker.
  const initialPeriod = (() => {
    if (existingUpdate) {
      // Period bounds are calendar dates, not moments in time — parse only
      // the date part (never `new Date(iso)`, which reintroduces the tz
      // shift bug where a 23:59:59.999Z end rolls into the next local day).
      const start = fromDateInputValue(isoToYmd(existingUpdate.weekStart));
      let end: Date;
      if (existingUpdate.periodEnd) {
        end = fromDateInputValue(isoToYmd(existingUpdate.periodEnd));
        end.setHours(23, 59, 59, 999);
      } else {
        end = getWeekEnd(start);
      }
      return {
        start,
        end,
        mode: isWeekSpan(toDateInputValue(start), toDateInputValue(end))
          ? ('week' as const)
          : ('custom' as const),
      };
    }
    const start = getWeekStart(new Date());
    return { start, end: getWeekEnd(start), mode: 'week' as const };
  })();

  const [periodMode, setPeriodMode] = useState<'week' | 'custom'>(initialPeriod.mode);
  const [periodStart, setPeriodStart] = useState<Date>(initialPeriod.start);
  const [periodEnd, setPeriodEnd] = useState<Date>(initialPeriod.end);

  const periodRangeInvalid = periodMode === 'custom' && periodStart.getTime() > periodEnd.getTime();
  const periodDays = daysInPeriod(toDateInputValue(periodStart), toDateInputValue(periodEnd));
  const periodTooLong = periodMode === 'custom' && periodDays > MAX_CUSTOM_RANGE_DAYS;
  const periodValid = !periodRangeInvalid && !periodTooLong;

  const periodLabel = formatPeriodLabel(
    toDateInputValue(periodStart),
    toDateInputValue(periodEnd),
    t('status_compose_week_of', { defaultValue: 'Week of' }),
    (week) => t('status_compose_week_number', { week, defaultValue: `KW ${week}` }),
  );

  // ── Period-scoped metrics (posts published + top-post candidates) ──
  const metricsStartIso = periodValid ? `${toDateInputValue(periodStart)}T00:00:00.000Z` : null;
  const metricsEndIso = periodValid ? `${toDateInputValue(periodEnd)}T23:59:59.999Z` : null;
  const { data: metrics } = useStatusUpdateMetrics(
    isOpen && client ? client.id : null,
    metricsStartIso,
    metricsEndIso,
  );

  // Real top posts only — when none are available the picker shows an
  // empty state below instead of fabricated demo data. Seeded from the
  // `availableTopPosts` prop (current-week data the parent already has) so
  // the very first paint doesn't flash empty while the period-scoped
  // metrics request is in flight; once metrics resolve for ANY period
  // (initial or user-selected), they become the source of truth.
  const [topPostCandidates, setTopPostCandidates] = useState<TopPostCandidate[]>(availableTopPosts);

  // Brand-coloured CSS variables fall back to default-theme hex if the
  // parser can't extract two stops from the supplied gradient string.
  const FALLBACK_FROM = 'var(--color-primary-400, #84d3ce)';
  const FALLBACK_TO = 'var(--color-primary-600, #4ab5ae)';
  const pickerPosts: TopPostDisplay[] = topPostCandidates.map((p) => {
    const gradients = p.thumbnailGradient.match(/#[0-9a-fA-F]{6}/g) ?? [];
    return {
      id: p.id,
      caption: p.caption?.trim() ? p.caption : t('common:untitled_post'),
      metric: p.metric,
      er: p.engagementRate !== undefined ? `${p.engagementRate}%` : '—',
      gradientFrom: gradients[0] ?? FALLBACK_FROM,
      gradientTo: gradients[1] ?? FALLBACK_TO,
    };
  });

  // ── Form state ──
  const [colorPill, setColorPill] = useState<ColorPill | null>(null);
  const [tldr, setTldr] = useState('');
  const [vibe, setVibe] = useState<VibeType | null>(null);
  // Output auto-fills from real data: published = posts published in the
  // selected period, planned = team's cadence target scaled to the period
  // length. The user can override via the Edit button — `*Touched` flags
  // track that so period changes don't clobber a manual edit. An existing
  // saved update takes precedence over both — that way reopening a
  // published update shows what was logged.
  const [postsPublished, setPostsPublished] = useState<number>(
    existingUpdate?.postsPublished ?? (velocityLocked ? 0 : client.velocity?.postsThisWeek ?? 0),
  );
  const [postsPlanned, setPostsPlanned] = useState<number>(
    existingUpdate?.postsPlanned ?? (cadenceLocked ? 0 : client.cadenceWeekly ?? 0),
  );
  const [postsPublishedTouched, setPostsPublishedTouched] = useState(false);
  const [postsPlannedTouched, setPostsPlannedTouched] = useState(false);
  const [editingOutput, setEditingOutput] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState<string>((pickerPosts[0]?.id ?? ''));
  const [showPostPicker, setShowPostPicker] = useState(false);
  const [wins, setWins] = useState('');
  const [blockers, setBlockers] = useState('');
  const [nextFocus, setNextFocus] = useState('');
  const [strategicNote, setStrategicNote] = useState('');

  // ── UI state ──
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const autoSaveTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // Historical updates live in the dedicated StatusUpdateHistoryModal — opened
  // via the `onShowHistory` prop wired to the icon button in the modal header.
  const selectedPost = pickerPosts.find((p) => p.id === selectedPostId) ?? pickerPosts[0];

  // ── Load draft or existingUpdate on open ──
  useEffect(() => {
    if (!isOpen) return;

    if (existingUpdate) {
      // Period bounds are calendar dates, not moments in time — parse only
      // the date part. `new Date(iso)` here would reintroduce the tz-shift
      // bug where a stored `...T23:59:59.999Z` end rolls into the next
      // local day (e.g. 31.12 displaying/gating as 01.01).
      const start = fromDateInputValue(isoToYmd(existingUpdate.weekStart));
      let end: Date;
      if (existingUpdate.periodEnd) {
        end = fromDateInputValue(isoToYmd(existingUpdate.periodEnd));
        end.setHours(23, 59, 59, 999);
      } else {
        end = getWeekEnd(start);
      }
      setPeriodStart(start);
      setPeriodEnd(end);
      setPeriodMode(isWeekSpan(toDateInputValue(start), toDateInputValue(end)) ? 'week' : 'custom');
      setColorPill(existingUpdate.colorPill);
      setTldr(existingUpdate.tldr);
      setVibe(existingUpdate.vibe ?? null);
      setPostsPublished(existingUpdate.postsPublished);
      setPostsPlanned(existingUpdate.postsPlanned);
      setSelectedPostId(existingUpdate.topPostId ?? (pickerPosts[0]?.id ?? ''));
      setWins(existingUpdate.wins ?? '');
      setBlockers(existingUpdate.blockers ?? '');
      setNextFocus(existingUpdate.nextFocus ?? '');
      setStrategicNote(existingUpdate.strategicNote ?? '');
      setIsDirty(false);
      return;
    }

    // Try localStorage draft
    const draft = loadDraft(client.id);
    if (draft) {
      if (draft.weekStart) {
        // Same date-part-only parsing as `existingUpdate` above — the draft
        // stores plain `YYYY-MM-DD` strings, but treat them identically so a
        // future format change (e.g. full ISO) can't reintroduce the shift.
        const start = fromDateInputValue(isoToYmd(draft.weekStart));
        let end: Date;
        if (draft.periodEnd) {
          end = fromDateInputValue(isoToYmd(draft.periodEnd));
          end.setHours(23, 59, 59, 999);
        } else {
          end = getWeekEnd(start);
        }
        setPeriodStart(start);
        setPeriodEnd(end);
        setPeriodMode(isWeekSpan(toDateInputValue(start), toDateInputValue(end)) ? 'week' : 'custom');
      }
      if (draft.colorPill) setColorPill(draft.colorPill);
      if (draft.tldr) setTldr(draft.tldr);
      if (draft.vibe) setVibe(draft.vibe);
      // Explicit values in the draft mean the user (or a prior auto-fill) had
      // already settled on a number — mark touched so a fresh metrics fetch
      // for the restored period doesn't silently overwrite it.
      if (draft.postsPublished !== undefined) {
        setPostsPublished(draft.postsPublished);
        setPostsPublishedTouched(true);
      }
      if (draft.postsPlanned !== undefined) {
        setPostsPlanned(draft.postsPlanned);
        setPostsPlannedTouched(true);
      }
      if (draft.topPostId) setSelectedPostId(draft.topPostId);
      if (draft.wins) setWins(draft.wins);
      if (draft.blockers) setBlockers(draft.blockers);
      if (draft.nextFocus) setNextFocus(draft.nextFocus);
      if (draft.strategicNote) setStrategicNote(draft.strategicNote);
      setIsDirty(false); // Loaded from draft = not new dirty
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, client.id, existingUpdate]);

  // ── Recompute top-post candidates from period-scoped metrics ──
  // Fires for ANY period (initial current-week or a later user selection).
  // Once metrics have resolved once, `topPostCandidates` never falls back to
  // the `availableTopPosts` prop again — only the very first paint (before
  // the first metrics response) uses it, to avoid a content flash.
  useEffect(() => {
    if (!metrics) return;
    const mapped: TopPostCandidate[] = metrics.topPosts.map((p) => ({
      id: p.id,
      caption: p.caption?.trim() ? p.caption : t('common:untitled_post'),
      metric: p.metric,
      engagementRate: p.engagementRate,
      thumbnailGradient: p.thumbnailPlaceholderGradient,
    }));
    setTopPostCandidates(mapped);
    if (!existingUpdate) {
      setSelectedPostId((prev) => (mapped.some((p) => p.id === prev) ? prev : (mapped[0]?.id ?? '')));
    }
  }, [metrics, existingUpdate]);

  // ── Recompute output auto-fill from period-scoped metrics ──
  // Only when composing a brand-new update (no existingUpdate) and the user
  // hasn't manually overridden the field via the Edit control.
  useEffect(() => {
    if (existingUpdate || !metrics) return;
    if (!postsPublishedTouched) {
      setPostsPublished(metrics.postsPublished);
    }
    if (!postsPlannedTouched && !cadenceLocked) {
      const weeks = Math.max(
        1,
        Math.ceil(daysInPeriod(toDateInputValue(periodStart), toDateInputValue(periodEnd)) / 7),
      );
      setPostsPlanned(client.cadenceWeekly * weeks);
    }
  }, [
    metrics,
    existingUpdate,
    postsPublishedTouched,
    postsPlannedTouched,
    periodStart,
    periodEnd,
    client.cadenceWeekly,
    cadenceLocked,
  ]);

  // ── Auto-save every 5s when dirty ──
  useEffect(() => {
    if (!isOpen) return;

    if (autoSaveTimer.current) clearInterval(autoSaveTimer.current);

    autoSaveTimer.current = setInterval(() => {
      if (isDirty) {
        saveDraft(client.id, {
          clientId: String(client.id),
          weekStart: toDateInputValue(periodStart),
          periodEnd: toDateInputValue(periodEnd),
          colorPill: colorPill ?? undefined,
          tldr,
          vibe: vibe ?? undefined,
          postsPublished,
          postsPlanned,
          topPostId: selectedPostId,
          wins: wins || undefined,
          blockers: blockers || undefined,
          nextFocus: nextFocus || undefined,
          strategicNote: strategicNote || undefined,
        });
      }
    }, 5000);

    return () => {
      if (autoSaveTimer.current) clearInterval(autoSaveTimer.current);
    };
  }, [
    isOpen,
    isDirty,
    client.id,
    periodStart,
    periodEnd,
    colorPill,
    tldr,
    vibe,
    postsPublished,
    postsPlanned,
    selectedPostId,
    wins,
    blockers,
    nextFocus,
    strategicNote,
  ]);

  // ── ESC key handler + body scroll lock ──
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      const ke = e as unknown as KeyboardEvent;
      // An inner control (emoji suggestion list, select, popover) that consumed Esc calls preventDefault();
      // one Esc = one layer, so the modal must not also ask to discard.
      if (ke.key === 'Escape' && !ke.defaultPrevented) {
        handleRequestClose();
      }
    };
    document.addEventListener('keydown', handler as EventListener);
    const unlockScroll = lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', handler as EventListener);
      unlockScroll();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isDirty]);

  // ── Cmd/Ctrl+Enter publish ──
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      const ke = e as unknown as KeyboardEvent;
      if ((ke.metaKey || ke.ctrlKey) && ke.key === 'Enter') {
        if (canPublish) {
          void handlePublish();
        }
      }
    };
    document.addEventListener('keydown', handler as EventListener);
    return () => document.removeEventListener('keydown', handler as EventListener);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, colorPill, tldr]);

  // ── Mark dirty on any field change ──
  const markDirty = useCallback(() => setIsDirty(true), []);

  // ── Period picker handlers ──
  const stepWeek = useCallback((direction: -1 | 1) => {
    setPeriodStart((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + direction * 7);
      return next;
    });
    setPeriodEnd((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + direction * 7);
      return next;
    });
    markDirty();
  }, [markDirty]);

  const handlePeriodModeChange = useCallback((mode: 'week' | 'custom') => {
    setPeriodMode(mode);
    if (mode === 'week') {
      // Snap to the Monday-anchored week containing the current period start
      setPeriodStart((prev) => {
        const snapped = getWeekStart(prev);
        setPeriodEnd(getWeekEnd(snapped));
        return snapped;
      });
    }
    markDirty();
  }, [markDirty]);

  // `DateRangePicker` hands back plain calendar-cell `Date`s (or null when a
  // bound is cleared) — re-derive the calendar date via
  // `toDateInputValue`/`fromDateInputValue` so the stored bounds stay
  // local-midnight / local-end-of-day, matching the rest of the component's
  // TZ-safe serialization path. Each bound updates independently so a
  // single-sided selection doesn't crash on the other being null.
  const handleRangeSelect = useCallback((range: { from: Date | null; to: Date | null }) => {
    if (range.from) {
      setPeriodStart(fromDateInputValue(toDateInputValue(range.from)));
    }
    if (range.to) {
      const end = fromDateInputValue(toDateInputValue(range.to));
      end.setHours(23, 59, 59, 999);
      setPeriodEnd(end);
    }
    if (range.from || range.to) markDirty();
  }, [markDirty]);

  // ── Validation ──
  const canPublish = colorPill !== null && tldr.trim().length > 0 && periodValid;

  // ── Build draft object ──
  const buildDraft = useCallback((): StatusUpdateDraft => ({
    clientId: String(client.id),
    weekStart: toDateInputValue(periodStart),
    periodEnd: toDateInputValue(periodEnd),
    colorPill: colorPill as ColorPill,
    tldr: tldr.trim(),
    vibe: vibe ?? undefined,
    postsPublished,
    postsPlanned,
    topPostId: selectedPostId,
    wins: wins.trim() || undefined,
    blockers: blockers.trim() || undefined,
    nextFocus: nextFocus.trim() || undefined,
    strategicNote: strategicNote.trim() || undefined,
  }), [
    client.id,
    periodStart,
    periodEnd,
    colorPill,
    tldr,
    vibe,
    postsPublished,
    postsPlanned,
    selectedPostId,
    wins,
    blockers,
    nextFocus,
    strategicNote,
  ]);

  // ── Reset form ──
  const resetForm = useCallback(() => {
    const resetStart = getWeekStart(new Date());
    setPeriodMode('week');
    setPeriodStart(resetStart);
    setPeriodEnd(getWeekEnd(resetStart));
    setColorPill(null);
    setTldr('');
    setVibe(null);
    setPostsPublished(velocityLocked ? 0 : client.velocity?.postsThisWeek ?? 0);
    setPostsPlanned(cadenceLocked ? 0 : client.cadenceWeekly ?? 0);
    setPostsPublishedTouched(false);
    setPostsPlannedTouched(false);
    setEditingOutput(false);
    setSelectedPostId((pickerPosts[0]?.id ?? ''));
    setShowPostPicker(false);
    setWins('');
    setBlockers('');
    setNextFocus('');
    setStrategicNote('');
    setIsDirty(false);
    setShowDiscardConfirm(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  // ── Handle close request (check for dirty) ──
  const handleRequestClose = useCallback(() => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      resetForm();
      onClose();
    }
  }, [isDirty, resetForm, onClose]);

  // ── Discard and close ──
  const handleDiscard = useCallback(() => {
    clearDraft(client.id);
    resetForm();
    onClose();
  }, [client.id, resetForm, onClose]);

  // ── Save as draft (local only; no server-side draft endpoint yet) ──
  const handleSaveDraft = useCallback(() => {
    setSavingDraft(true);
    saveDraft(client.id, buildDraft());
    setSavingDraft(false);
    setIsDirty(false);
    showToast(
      t('status_compose_draft_saved', { defaultValue: 'Draft saved' }),
      'success'
    );
    resetForm();
    onClose();
  }, [buildDraft, client.id, onClose, resetForm, t]);

  // ── Publish ──
  // Real persistence lives in the parent (`useSaveStatusUpdate` mutation
  // wired into `onSave`). We let the parent handle the loading state from
  // the actual mutation; this handler just fires it and closes the modal.
  const handlePublish = useCallback(async () => {
    if (!canPublish) return;
    setSaving(true);
    try {
      const draft = buildDraft();
      if (onSave) await onSave(draft);
      clearDraft(client.id);
      setIsDirty(false);
      showToast(
        t('status_compose_published', { defaultValue: 'Status update published!' }),
        'success'
      );
      resetForm();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish';
      showToast(message, 'error');
    } finally {
      setSaving(false);
    }
  }, [canPublish, buildDraft, client.id, onSave, onClose, resetForm, t]);

  // Register this editor's dirty state globally while it is open, so the
  // auto-reload mechanism defers to a manual refresh banner instead of
  // silently discarding in-progress work. Parent conditionally mounts this
  // modal (only when a composeClient is set, always with isOpen=true), and
  // useUnsavedGuard auto-clears on unmount, so no extra isOpen gating needed.
  useUnsavedGuard(isDirty, 'status-update-compose');

  if (!isOpen) return null;

  const lifecycleBg =
    client.lifecycleStage === 'active'
      ? 'bg-primary-50 text-primary-700'
      : client.lifecycleStage === 'onboarding'
        ? 'bg-peach/30 text-coral-700'
        : client.lifecycleStage === 'churning'
          ? 'bg-coral-50 text-coral-700'
          : 'bg-gray-100 text-sage-gray';

  const lifecycleLabel =
    client.lifecycleStage === 'active'
      ? 'Active'
      : client.lifecycleStage === 'onboarding'
        ? 'Onboarding'
        : client.lifecycleStage === 'churning'
          ? 'Churning'
          : 'Paused';

  return (
    <>
      {/* ── Discard confirm overlay ─────────────────────────────────────── */}
      {showDiscardConfirm && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 10000 }}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="discard-title"
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowDiscardConfirm(false)}
            aria-hidden="true"
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-[calc(100vw-2rem)] max-w-sm mx-4 z-10">
            <h3 id="discard-title" className="text-base font-bold text-gray-900 mb-2">
              {t('status_compose_discard_title', { defaultValue: 'Discard changes?' })}
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {t('status_compose_discard_desc', {
                defaultValue: 'Your unsaved changes will be lost. This cannot be undone.',
              })}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
                onClick={() => setShowDiscardConfirm(false)}
              >
                {t('status_compose_keep_editing', { defaultValue: 'Keep editing' })}
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-coral-contrast bg-coral-500 rounded-lg hover:bg-coral-600 transition-colors border border-transparent focus:outline-none focus-visible:border-gray-900"
                onClick={handleDiscard}
              >
                {t('status_compose_discard_confirm', { defaultValue: 'Discard' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal backdrop + container ───────────────────────────────────── */}
      <div
        className="fixed left-0 right-0 top-0 bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom))] lg:inset-0 flex items-end sm:items-center justify-center"
        style={{ zIndex: backdropZ }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="compose-modal-title"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          onClick={handleRequestClose}
          aria-hidden="true"
        />

        {/* Panel - bottom sheet on mobile, centered modal on desktop */}
        <div
          style={{ zIndex: contentZ }}
          className="
            relative bg-white flex flex-col shadow-2xl
            w-full h-full rounded-t-2xl rounded-b-none
            sm:h-auto sm:max-h-[calc(100vh-4rem)] sm:w-[calc(100vw-2rem)] sm:max-w-2xl sm:mx-0 sm:rounded-2xl
            overflow-hidden
          "
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && canPublish) {
              e.preventDefault();
              void handlePublish();
            }
          }}
        >
          {/* ── Drag handle (mobile only) ─────────────────────────────── */}
          <div className="sm:hidden flex justify-center pt-3 pb-1 shrink-0">
            <div
              className="w-10 h-1 rounded-full bg-gray-300"
              aria-hidden="true"
            />
          </div>

          {/* ── Sticky Header ─────────────────────────────────────────── */}
          <div className="shrink-0 border-b border-gray-900/10 px-4 sm:px-5 py-3">
            {/* Wagner overline */}
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-medium mb-1">
              {t('status_compose_overline', { defaultValue: 'Status update' })}
            </p>
            <div className="flex items-start gap-3">
              {/* Client avatar — logo / icon+color / palette-letter cascade,
                  same pattern as the rail, drawer, and history modal. */}
              {client.logoUrl ? (
                <img
                  src={client.logoUrl}
                  alt={client.name}
                  className="w-9 h-9 rounded-lg object-cover shrink-0"
                />
              ) : client.icon ? (
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    client.customColor ? '' : 'bg-primary-500'
                  }`}
                  style={
                    client.customColor ? { backgroundColor: client.customColor } : undefined
                  }
                  aria-hidden="true"
                >
                  <i
                    className={`hm hm-${client.icon} ${client.customColor ? getContrastTextColor(client.customColor) : 'text-white'}`}
                    style={{ fontSize: 18 }}
                    aria-hidden="true"
                  />
                </div>
              ) : (
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold ${avatarTextClass(client.avatarColor)} text-sm shrink-0 ${client.avatarColor}`}
                  aria-hidden="true"
                >
                  {client.avatarInitial}
                </div>
              )}

              {/* Client info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2
                    id="compose-modal-title"
                    className="font-wagner text-xl leading-tight tracking-tight text-gray-900"
                  >
                    {client.name}
                  </h2>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${lifecycleBg}`}
                  >
                    {lifecycleLabel}
                  </span>
                </div>

                {/* ── Period picker: Week (stepper) or Custom range ─────── */}
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <div
                    className="inline-flex items-center rounded-full bg-gray-100 p-0.5 text-[10px] font-semibold shrink-0"
                    role="radiogroup"
                    aria-label={t('status_compose_period_mode_label', { defaultValue: 'Period type' })}
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={periodMode === 'week'}
                      className={`px-[7px] py-px rounded-full transition-colors outline-none border border-transparent focus-visible:border-primary-600 ${
                        periodMode === 'week'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                      onClick={() => handlePeriodModeChange('week')}
                    >
                      {t('status_compose_period_week', { defaultValue: 'Week' })}
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={periodMode === 'custom'}
                      className={`px-[7px] py-px rounded-full transition-colors outline-none border border-transparent focus-visible:border-primary-600 ${
                        periodMode === 'custom'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                      onClick={() => handlePeriodModeChange('custom')}
                    >
                      {t('status_compose_period_custom', { defaultValue: 'Custom range' })}
                    </button>
                  </div>

                  {periodMode === 'week' ? (
                    <div className="flex items-center gap-0.5">
                      <button
                        type="button"
                        className="w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors outline-none border border-transparent focus-visible:border-primary-600"
                        onClick={() => stepWeek(-1)}
                        aria-label={t('status_compose_period_prev_week', { defaultValue: 'Previous week' })}
                      >
                        <i className="hm hm-angle-left" style={{ fontSize: 9 }} aria-hidden="true" />
                      </button>
                      <span className="text-xs text-gray-400 whitespace-nowrap">{periodLabel}</span>
                      <button
                        type="button"
                        className="w-5 h-5 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors outline-none border border-transparent focus-visible:border-primary-600"
                        onClick={() => stepWeek(1)}
                        aria-label={t('status_compose_period_next_week', { defaultValue: 'Next week' })}
                      >
                        <i className="hm hm-angle-right" style={{ fontSize: 9 }} aria-hidden="true" />
                      </button>
                    </div>
                  ) : (
                    <DateRangePicker
                      value={{ from: periodStart, to: periodEnd }}
                      onChange={handleRangeSelect}
                      size="sm"
                    />
                  )}
                </div>
                {periodMode === 'custom' && (periodRangeInvalid || periodTooLong) && (
                  <p className="text-[11px] text-coral-600 mt-1" role="alert">
                    {periodRangeInvalid
                      ? t('status_compose_period_invalid_range', {
                          defaultValue: 'From date must be on or before the To date.',
                        })
                      : t('status_compose_period_too_long', {
                          defaultValue: `Custom range can span at most ${MAX_CUSTOM_RANGE_DAYS} days.`,
                        })}
                  </p>
                )}
              </div>

              {/* History (opens dedicated history modal) + close */}
              <div className="flex items-center gap-1.5 shrink-0">
                {onShowHistory && hasHistory && (
                  <Tooltip
                    content={t('status_compose_history_tooltip', {
                      defaultValue: 'View status update history',
                    })}
                    side="bottom"
                    delayDuration={300}
                  >
                    <button
                      type="button"
                      className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-primary-600 hover:bg-gray-100 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
                      onClick={onShowHistory}
                      aria-label={t('status_compose_history_aria', {
                        defaultValue: 'Open status update history',
                      })}
                    >
                      <i className="hm hm-refresh-time" style={{ fontSize: 15 }} aria-hidden="true" />
                    </button>
                  </Tooltip>
                )}
                <button
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors outline-none border border-transparent focus-visible:border-primary-600"
                  onClick={handleRequestClose}
                  aria-label={t('status_compose_close', { defaultValue: 'Close' })}
                >
                  <i className="hm hm-cross" style={{ fontSize: 15 }} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {/* ── Scrollable Body ───────────────────────────────────────── */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto">
            {/* History lives in its own dedicated modal — opened via the
             *  `onShowHistory` prop wired to the icon button in the header. */}

            <div className="px-4 sm:px-5 py-5 space-y-5">
              {/* ── 1. Color pill selector ─────────────────────────────── */}
              <section aria-labelledby="section-color-pill">
                <p
                  id="section-color-pill"
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  {t('status_compose_status_label', { defaultValue: 'Status' })}
                  <span className="text-coral-500 ml-0.5" aria-hidden="true">*</span>
                </p>
                <div
                  className="flex flex-wrap gap-2"
                  role="radiogroup"
                  aria-label={t('status_compose_status_label', { defaultValue: 'Status' })}
                >
                  {PILL_CONFIGS.map((pill) => {
                    const isSelected = colorPill === pill.value;
                    const pillButton = (
                      <button
                        key={pill.value}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        className={`
                          flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold
                          border-2 transition-all duration-150 cursor-pointer
                          outline-none focus-visible:border-gray-900
                          ${isSelected
                            ? `${pill.selectedBg} ${pill.text} border-current shadow-sm`
                            : `${pill.bg} ${pill.text} border-transparent hover:border-current hover:shadow-sm`
                          }
                        `}
                        onClick={() => {
                          setColorPill(pill.value);
                          markDirty();
                        }}
                      >
                        <span aria-hidden="true">{pill.emoji}</span>
                        {statusPillLabel(t, pill.value)}
                      </button>
                    );
                    if (pill.glossaryTermId) {
                      return (
                        <GlossaryTooltip key={pill.value} termId={pill.glossaryTermId} side="top" frameShape="pill">
                          {pillButton}
                        </GlossaryTooltip>
                      );
                    }
                    return (
                      <Tooltip key={pill.value} content={t(pill.tooltip)} side="top" delayDuration={300}>
                        {pillButton}
                      </Tooltip>
                    );
                  })}
                </div>
              </section>

              {/* ── 2. TL;DR ──────────────────────────────────────────── */}
              <section aria-labelledby="section-tldr">
                <p
                  id="section-tldr"
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  {t('status_compose_tldr_label', { defaultValue: 'TL;DR' })}
                  <span className="text-coral-500 ml-0.5" aria-hidden="true">*</span>
                </p>
                <EmojiAutocompleteField
                  id="tldr-input"
                  value={tldr}
                  onChange={(next) => {
                    setTldr(next.slice(0, 200));
                    markDirty();
                  }}
                  placeholder={t('status_compose_tldr_placeholder', {
                    defaultValue: 'One-line headline for the week (what matters most)',
                  })}
                  maxLength={200}
                  aria-describedby="tldr-count"
                  trailingSlot={
                    <span
                      id="tldr-count"
                      className={`text-xs tabular-nums ${
                        tldr.length >= 180 ? 'text-coral-500 font-semibold' : 'text-gray-400'
                      }`}
                      aria-live="polite"
                      aria-label={t('status_compose_tldr_count_aria', { chars: tldr.length, max: 200 })}
                    >
                      {tldr.length}/200
                    </span>
                  }
                />
              </section>

              {/* ── 3. Vibe selector ──────────────────────────────────── */}
              <section aria-labelledby="section-vibe">
                <p
                  id="section-vibe"
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  {t('status_compose_vibe_label', { defaultValue: 'Vibe' })}
                  <span className="text-gray-300 ml-1 text-[10px] font-normal normal-case">
                    {t('status_compose_optional', { defaultValue: 'optional' })}
                  </span>
                </p>
                <div
                  className="grid grid-cols-4 gap-2"
                  role="radiogroup"
                  aria-label={t('status_compose_vibe_label', { defaultValue: 'Vibe' })}
                >
                  {VIBE_CONFIGS.map((v) => {
                    const isSelected = vibe === v.value;
                    return (
                      <Tooltip key={v.value} content={t(v.tooltip)} side="top" delayDuration={300}>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={isSelected}
                          className={`
                            flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all duration-150
                            cursor-pointer border border-transparent focus:outline-none focus-visible:border-primary-600
                            hover:[&_span:first-child]:rotate-6
                            ${isSelected
                              ? `${v.selectedBg} ${v.selectedText} border-current shadow-sm`
                              : 'bg-gray-50 border-gray-100 hover:border-gray-300 text-gray-600'
                            }
                          `}
                          onClick={() => {
                            setVibe(isSelected ? null : v.value);
                            markDirty();
                          }}
                        >
                          <span className="text-2xl leading-none transition-transform duration-150 inline-block" aria-hidden="true">
                            {v.emoji}
                          </span>
                          <span className="text-xs font-medium">{statusVibeLabel(t, v.value)}</span>
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              </section>

              {/* ── 4. Output card ────────────────────────────────────── */}
              <section aria-labelledby="section-output">
                <p
                  id="section-output"
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  {t('status_compose_output_label', {
                    defaultValue: periodMode === 'week' ? "This week's output" : "This period's output",
                  })}
                </p>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  {!editingOutput ? (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-gray-900">
                            {postsPublished}
                          </span>
                          <span className="text-lg text-gray-400">/</span>
                          <span className="text-2xl font-bold text-gray-500">
                            {postsPlanned}
                          </span>
                          <span className="text-sm text-gray-500 ml-1">
                            {t('status_compose_posts_published', {
                              defaultValue: 'posts published',
                            })}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium underline underline-offset-2 border border-transparent focus:outline-none focus-visible:border-primary-600 rounded"
                          onClick={() => setEditingOutput(true)}
                        >
                          {t('status_compose_edit', { defaultValue: 'Edit' })}
                        </button>
                      </div>
                      <Tooltip content={t('status_compose_tooltip_output', { defaultValue: 'Posts published vs. planned - pulled from your data. Edit if needed.' })} side="top" delayDuration={300}>
                        <p className="text-xs text-gray-400 mt-1 cursor-default">
                          {t('status_compose_output_hint', {
                            defaultValue: 'Auto-filled from data - edit below if needed',
                          })}
                        </p>
                      </Tooltip>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label
                            className="text-xs text-gray-500 mb-1 block"
                            htmlFor="posts-published"
                          >
                            {t('status_compose_published', { defaultValue: 'Published' })}
                          </label>
                          <Input
                            id="posts-published"
                            type="number"
                            inputSize="sm"
                            min={0}
                            max={99}
                            value={postsPublished}
                            onChange={(e) => {
                              setPostsPublished(Math.max(0, parseInt(e.target.value, 10) || 0));
                              setPostsPublishedTouched(true);
                              markDirty();
                            }}
                          />
                        </div>
                        <div className="flex-1">
                          <label
                            className="text-xs text-gray-500 mb-1 block"
                            htmlFor="posts-planned"
                          >
                            {t('status_compose_planned', { defaultValue: 'Planned' })}
                          </label>
                          <Input
                            id="posts-planned"
                            type="number"
                            inputSize="sm"
                            min={0}
                            max={99}
                            value={postsPlanned}
                            onChange={(e) => {
                              setPostsPlanned(Math.max(0, parseInt(e.target.value, 10) || 0));
                              setPostsPlannedTouched(true);
                              markDirty();
                            }}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2 border border-transparent focus:outline-none focus-visible:border-primary-600 rounded"
                        onClick={() => setEditingOutput(false)}
                      >
                        {t('status_compose_done', { defaultValue: 'Done' })}
                      </button>
                    </div>
                  )}
                </div>
              </section>

              {/* ── 5. Top performer ──────────────────────────────────── */}
              <section aria-labelledby="section-top-post">
                <p
                  id="section-top-post"
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  <Tooltip content={t('status_compose_tooltip_top_performer', { defaultValue: 'Auto-suggested best post of the week based on engagement.' })} side="top" delayDuration={300}>
                    <span className="cursor-default">{t('status_compose_top_post_label', { defaultValue: 'Top performer' })}</span>
                  </Tooltip>
                  <span className="text-gray-300 ml-1 text-[10px] font-normal normal-case">
                    {t('status_compose_optional', { defaultValue: 'optional' })}
                  </span>
                </p>

                {!showPostPicker ? (
                  selectedPost ? (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-start gap-3">
                        {/* Thumbnail placeholder */}
                        <div
                          className={`w-14 h-14 rounded-lg bg-gradient-to-br ${selectedPost.gradientFrom} ${selectedPost.gradientTo} shrink-0 flex items-center justify-center`}
                          aria-hidden="true"
                        >
                          <i
                            className="hm hm-star text-white"
                            style={{ fontSize: 20 }}
                            aria-hidden="true"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700 leading-snug line-clamp-2 mb-1">
                            {selectedPost.caption}
                          </p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-primary-600">
                              {selectedPost.metric}
                            </span>
                            <span className="text-xs text-gray-400">·</span>
                            <span className="text-xs font-semibold text-primary-600">
                              {selectedPost.er}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          type="button"
                          className="flex-1 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600"
                          onClick={() => {
                            markDirty();
                            // Already selected, just confirm
                          }}
                        >
                          {t('status_compose_use_suggested', { defaultValue: 'Use suggested' })}
                        </button>
                        <button
                          type="button"
                          className="flex-1 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus-visible:border-primary-600"
                          onClick={() => setShowPostPicker(true)}
                        >
                          {t('status_compose_pick_different', {
                            defaultValue: 'Pick different',
                          })}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 text-center">
                      <p className="text-xs text-gray-400 italic">
                        {t('status_compose_no_top_posts', {
                          defaultValue: 'No top-performing posts found for this period.',
                        })}
                      </p>
                    </div>
                  )
                ) : (
                  <div className="rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                      <p className="text-xs font-medium text-gray-600">
                        {t('status_compose_pick_post', { defaultValue: 'Select a post' })}
                      </p>
                      <button
                        type="button"
                        className="text-xs text-gray-400 hover:text-gray-600 border border-transparent focus:outline-none focus-visible:border-primary-600 rounded"
                        onClick={() => setShowPostPicker(false)}
                      >
                        {t('status_compose_cancel', { defaultValue: 'Cancel' })}
                      </button>
                    </div>
                    <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
                      {pickerPosts.length === 0 && (
                        <div className="px-3 py-6 text-center text-xs text-gray-400">
                          {t('status_compose_no_top_posts', {
                            defaultValue: 'No top-performing posts found for this period.',
                          })}
                        </div>
                      )}
                      {pickerPosts.map((post) => (
                        <button
                          key={post.id}
                          type="button"
                          className={`w-full text-left flex items-start gap-3 p-3 hover:bg-gray-50 transition-colors border border-transparent focus:outline-none focus-visible:border-primary-600 ${
                            selectedPostId === post.id ? 'bg-primary-25' : ''
                          }`}
                          onClick={() => {
                            setSelectedPostId(post.id);
                            setShowPostPicker(false);
                            markDirty();
                          }}
                        >
                          <div
                            className={`w-10 h-10 rounded-lg bg-gradient-to-br ${post.gradientFrom} ${post.gradientTo} shrink-0`}
                            aria-hidden="true"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-700 leading-snug line-clamp-1">
                              {post.caption}
                            </p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-xs font-semibold text-primary-600">
                                {post.metric}
                              </span>
                              <span className="text-[10px] text-gray-400">·</span>
                              <span className="text-xs font-semibold text-primary-600">
                                {post.er}
                              </span>
                            </div>
                          </div>
                          {selectedPostId === post.id && (
                            <i
                              className="hm hm-tick-circle text-primary-500 shrink-0 mt-0.5"
                              style={{ fontSize: 16 }}
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </section>

              {/* ── 6. Wins ───────────────────────────────────────────── */}
              <section aria-labelledby="section-wins">
                <p
                  id="section-wins"
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  {t('status_compose_wins_label', { defaultValue: 'Wins' })}
                  <span className="text-gray-300 ml-1 text-[10px] font-normal normal-case">
                    {t('status_compose_optional', { defaultValue: 'optional' })}
                  </span>
                </p>
                <EmojiAutocompleteField
                  multiline
                  id="wins-textarea"
                  value={wins}
                  onChange={(next) => {
                    setWins(next);
                    markDirty();
                  }}
                  placeholder={t('status_compose_wins_placeholder', {
                    defaultValue: 'What went well? Celebrate specifics.',
                  })}
                  rows={3}
                  textareaClassName="resize-none"
                />
              </section>

              {/* ── 7. Blockers ───────────────────────────────────────── */}
              <section aria-labelledby="section-blockers">
                <p
                  id="section-blockers"
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  {t('status_compose_blockers_label', { defaultValue: 'Blockers' })}
                  <span className="text-gray-300 ml-1 text-[10px] font-normal normal-case">
                    {t('status_compose_optional', { defaultValue: 'optional' })}
                  </span>
                </p>
                <EmojiAutocompleteField
                  multiline
                  id="blockers-textarea"
                  value={blockers}
                  onChange={(next) => {
                    setBlockers(next);
                    markDirty();
                  }}
                  placeholder={t('status_compose_blockers_placeholder', {
                    defaultValue:
                      "What's in the way? Be specific - legal review, client delays, creative block…",
                  })}
                  rows={3}
                  textareaClassName="resize-none"
                />
              </section>

              {/* ── 8. Next week focus ────────────────────────────────── */}
              <section aria-labelledby="section-next-focus">
                <p
                  id="section-next-focus"
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  {t('status_compose_next_focus_label', {
                    defaultValue: 'Next week focus',
                  })}
                  <span className="text-gray-300 ml-1 text-[10px] font-normal normal-case">
                    {t('status_compose_optional', { defaultValue: 'optional' })}
                  </span>
                </p>
                <EmojiAutocompleteField
                  multiline
                  id="next-focus-textarea"
                  value={nextFocus}
                  onChange={(next) => {
                    setNextFocus(next);
                    markDirty();
                  }}
                  placeholder={t('status_compose_next_focus_placeholder', {
                    defaultValue: "What's the focus for next week?",
                  })}
                  rows={3}
                  textareaClassName="resize-none"
                />
              </section>

              {/* ── 9. Strategic note ─────────────────────────────────── */}
              <section aria-labelledby="section-strategic" className="pb-2">
                <p
                  id="section-strategic"
                  className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
                >
                  {t('status_compose_strategic_label', {
                    defaultValue: 'Strategic note',
                  })}
                  <span className="text-gray-300 ml-1 text-[10px] font-normal normal-case">
                    {t('status_compose_optional', { defaultValue: 'optional' })}
                  </span>
                </p>
                <EmojiAutocompleteField
                  multiline
                  id="strategic-textarea"
                  value={strategicNote}
                  onChange={(next) => {
                    setStrategicNote(next);
                    markDirty();
                  }}
                  placeholder={t('status_compose_strategic_placeholder', {
                    defaultValue:
                      'Big-picture signal (pillar shift, new platform, pivot). Optional.',
                  })}
                  rows={2}
                  textareaClassName="resize-none"
                />
              </section>
            </div>
          </div>

          {/* ── Sticky Footer ─────────────────────────────────────────── */}
          <div className="shrink-0 border-t border-gray-100 bg-white px-4 sm:px-5 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
            {/* Keyboard shortcut hint */}
            <p className="text-[10px] text-gray-400 text-right mb-2 hidden sm:block">
              <kbd className="px-1 py-0.5 text-[10px] bg-gray-100 rounded border border-gray-200 font-mono">
                ⌘
              </kbd>
              {' + '}
              <kbd className="px-1 py-0.5 text-[10px] bg-gray-100 rounded border border-gray-200 font-mono">
                ↵
              </kbd>{' '}
              {t('status_compose_keyboard_hint', { defaultValue: 'to publish' })}
            </p>
            <div className="flex items-center gap-2">
              {/* Save as draft */}
              <Tooltip content={t('status_compose_tooltip_save_draft', { defaultValue: 'Keep your progress. You can come back and publish later.' })} side="top" delayDuration={300}>
                <Button
                  variant="outline"
                  size="sm"
                  loading={savingDraft}
                  onClick={() => { void handleSaveDraft(); }}
                  className="flex-1 sm:flex-none"
                >
                  <i
                    className="hm hm-pen-pencil mr-1.5"
                    style={{ fontSize: 14 }}
                    aria-hidden="true"
                  />
                  {t('status_compose_save_draft', { defaultValue: 'Save as draft' })}
                </Button>
              </Tooltip>

              <div className="hidden sm:block flex-1" />

              {/* Publish */}
              <Tooltip content={t('status_compose_tooltip_publish', { defaultValue: 'Visible to your team and portfolio lead.' })} side="top" delayDuration={300}>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!canPublish}
                  loading={saving}
                  onClick={() => { void handlePublish(); }}
                  className="flex-1 sm:flex-none"
                >
                  <i
                    className="hm hm-paper-plane mr-1.5"
                    style={{ fontSize: 14 }}
                    aria-hidden="true"
                  />
                  {t('status_compose_publish', { defaultValue: 'Publish update' })}
                </Button>
              </Tooltip>
            </div>

            {!canPublish && (
              <p className="text-xs text-gray-400 text-right mt-1.5" role="status">
                {!periodValid
                  ? periodRangeInvalid
                    ? t('status_compose_period_invalid_range', {
                        defaultValue: 'From date must be on or before the To date.',
                      })
                    : t('status_compose_period_too_long', {
                        defaultValue: `Custom range can span at most ${MAX_CUSTOM_RANGE_DAYS} days.`,
                      })
                  : t('status_compose_publish_hint', {
                      defaultValue: 'Select a status and add a TL;DR to publish',
                    })}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StatusUpdateComposeModal;
